import type { ColumnDef } from '@tanstack/react-table';

import { type DateFilter, type GetUsersQuery, UserPermission, UserSource, UserState } from 'data/__generated__';
import { textColumn, multiSelectColumn, booleanColumn, dateRangeColumn } from 'components/DataTable';

import { StatusTag } from '../components/StatusTag';

export type UserRow = GetUsersQuery['users']['users'][number];

export type UsersFilterInput = {
    searchValue?: string;
    sources?: UserSource[];
    states?: UserState[];
    permissions?: UserPermission[];
    organizationName?: string;
    hasOrganization?: boolean;
    hasDevicePushToken?: boolean;
    hasStripeCustomerId?: boolean;
    hasPaygreenCustomerId?: boolean;
    createdAt?: DateFilter;
    lastSeenAt?: DateFilter;
};

const USER_SOURCE_LABELS: Record<UserSource, string> = {
    [UserSource.Totem]: 'Compte TOTEM',
    [UserSource.Sensei]: 'Sensei',
    [UserSource.Nayax]: 'Nayax',
    [UserSource.Nu]: 'Nu',
    [UserSource.Digitiz]: 'Digitiz',
    [UserSource.Odoo]: 'Odoo',
};

export const usersColumns: ColumnDef<UserRow, unknown>[] = [
    textColumn<UserRow, UsersFilterInput>({
        id: 'fullname',
        accessorFn: (row) => row.profile.fullname || 'non renseigné',
        header: 'Nom',
        defaultVisible: true,
    }),
    textColumn<UserRow, UsersFilterInput>({
        accessorKey: 'username',
        header: 'Username',
        defaultVisible: true,
    }),
    textColumn<UserRow, UsersFilterInput>({
        accessorKey: '_id',
        header: 'ID',
    }),
    multiSelectColumn<UserRow, UsersFilterInput>({
        accessorKey: 'source',
        header: 'Type',
        filterKey: 'sources',
        defaultVisible: true,
        options: Object.values(UserSource).map((source) => ({
            value: source,
            label: USER_SOURCE_LABELS[source],
        })),
        cell: ({ row }) => USER_SOURCE_LABELS[row.original.source],
    }),
    multiSelectColumn<UserRow, UsersFilterInput>({
        id: 'state',
        accessorFn: (row) => row.profile.state,
        header: 'Statut',
        filterKey: 'states',
        defaultVisible: true,
        options: Object.values(UserState).map((state) => ({ value: state, label: state })),
        renderOption: (option) => <StatusTag state={option.value as UserState} />,
        cell: ({ row }) => {
            const state = row.original.profile.state as UserState | null | undefined;
            return state ? <StatusTag state={state} /> : <span>inconnu</span>;
        },
    }),
    multiSelectColumn<UserRow, UsersFilterInput>({
        id: 'permissions',
        accessorFn: (row) => row.permissions.map((p) => p.userGroup).join(', ') || '—',
        header: 'Rôles',
        filterKey: 'permissions',
        options: Object.values(UserPermission).map((p) => ({ value: p, label: p })),
        cell: ({ row }) => row.original.permissions.map((p) => p.userGroup).join(', ') || '—',
    }),
    textColumn<UserRow, UsersFilterInput>({
        id: 'organizationName',
        accessorFn: (row) => row.organization?.name ?? '',
        header: 'Organisation',
        filterKey: 'organizationName',
    }),
    booleanColumn<UserRow, UsersFilterInput>({
        id: 'hasOrganization',
        accessorFn: (row) => !!row.profile.organizationId,
        header: 'A une organisation',
        filterKey: 'hasOrganization',
    }),
    textColumn<UserRow, UsersFilterInput>({
        id: 'lastSiteName',
        accessorFn: (row) => row.lastSite?.name ?? '',
        header: 'Dernier site',
        defaultVisible: true,
    }),
    dateRangeColumn<UserRow, UsersFilterInput>({
        accessorKey: 'createdAt',
        header: 'Date de création',
        filterKey: 'createdAt',
    }),
    dateRangeColumn<UserRow, UsersFilterInput>({
        accessorKey: 'lastSeenAt',
        header: 'Dernière connexion',
        defaultVisible: true,
        filterKey: 'lastSeenAt',
        cell: ({ getValue }) => {
            const value = getValue() as number | null | undefined;
            return value ? new Date(value).toLocaleString() : 'jamais';
        },
    }),
];
